import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { StaffCard } from "@/components/StaffCard";
import { QRCodeCard } from "@/components/QRCodeCard";
import { AddStaffDialog } from "@/components/AddStaffDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Plus, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { getPlanLimits } from "@shared/plans";
import type { Staff, Business, ReviewRequest, Interaction } from "@shared/schema";

export default function StaffPage() {
  const { toast } = useToast();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState<Staff | null>(null);

  const { data: business } = useQuery<Business>({
    queryKey: ["/api/business"],
  });

  const { data: staffList, isLoading } = useQuery<Staff[]>({
    queryKey: ["/api/staff"],
  });

  // Fetch review requests and interactions for stats calculation
  const { data: reviewRequests } = useQuery<ReviewRequest[]>({
    queryKey: ["/api/review-requests"],
  });

  const { data: interactions } = useQuery<Interaction[]>({
    queryKey: ["/api/interactions"],
  });

  // Calculate stats per staff member
  const staffStats = useMemo(() => {
    const stats: Record<string, { sent: number; responses: number }> = {};
    
    // Count requests sent per staff
    reviewRequests?.forEach((req) => {
      if (!stats[req.staffId]) {
        stats[req.staffId] = { sent: 0, responses: 0 };
      }
      stats[req.staffId].sent++;
    });

    // Count responses (interactions with ratings) per staff
    interactions?.forEach((interaction) => {
      if (interaction.staffId && interaction.rating) {
        if (!stats[interaction.staffId]) {
          stats[interaction.staffId] = { sent: 0, responses: 0 };
        }
        stats[interaction.staffId].responses++;
      }
    });

    return stats;
  }, [reviewRequests, interactions]);

  const updateStaffMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      return apiRequest("PATCH", `/api/staff/${id}`, { isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/staff"] });
    },
  });

  const addStaffMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", "/api/staff", { name });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error?.message || "Failed to add staff member");
      }
      return res;
    },
    onSuccess: (_, name) => {
      queryClient.invalidateQueries({ queryKey: ["/api/staff"] });
      queryClient.invalidateQueries({ queryKey: ["/api/business"] });
      toast({
        title: "Staff added",
        description: `${name} has been added to your team`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Calculate staff limit status
  const planLimits = business ? getPlanLimits(business.planType || "free") : null;
  const currentStaffCount = staffList?.length || 0;
  const maxStaff = planLimits?.maxStaff || 3;
  const remaining = maxStaff === Infinity ? Infinity : Math.max(0, maxStaff - currentStaffCount);
  const isAtLimit = remaining === 0;
  const isApproachingLimit = remaining !== Infinity && remaining === 1 && currentStaffCount > 0;

  const deleteStaffMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/staff/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/staff"] });
      toast({
        title: "Staff removed",
        description: `${staffToDelete?.name} has been removed from your team`,
      });
      setStaffToDelete(null);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to remove staff member",
        variant: "destructive",
      });
    },
  });

  const handleToggleActive = (id: string, isActive: boolean) => {
    const member = staffList?.find((s) => s.id === id);
    updateStaffMutation.mutate({ id, isActive });
    toast({
      title: isActive ? "Staff activated" : "Staff deactivated",
      description: `${member?.name} is now ${isActive ? "active" : "inactive"}`,
    });
  };

  const handleShowQR = (id: string) => {
    const member = staffList?.find((s) => s.id === id);
    if (member) {
      setSelectedStaff(member);
      setQrDialogOpen(true);
    }
  };

  const handleEdit = (id: string) => {
    toast({
      title: "Edit mode",
      description: "Staff editing would open here",
    });
  };

  const handleAddStaff = (name: string) => {
    addStaffMutation.mutate(name);
  };

  const handleDelete = (id: string) => {
    const member = staffList?.find((s) => s.id === id);
    if (member) {
      setStaffToDelete(member);
      setDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = () => {
    if (staffToDelete) {
      deleteStaffMutation.mutate(staffToDelete.id);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-semibold">Staff Members</h1>
          <p className="text-muted-foreground mt-1">
            Manage your team and their review collection
          </p>
        </div>
        <Button 
          onClick={() => setAddDialogOpen(true)} 
          disabled={isAtLimit}
          data-testid="button-add-staff"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Staff
        </Button>
      </div>

      {/* Staff limit warning */}
      {isAtLimit && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Staff limit reached</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>
              You've reached the maximum of {maxStaff} staff member{maxStaff !== 1 ? "s" : ""} for your plan.
              Upgrade to add more team members.
            </span>
            <Button variant="outline" size="sm" asChild className="ml-4 shrink-0">
              <Link href="/pricing">Upgrade</Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isApproachingLimit && (
        <Alert className="mb-6 border-yellow-500/50 bg-yellow-500/10">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertTitle className="text-yellow-600">Approaching staff limit</AlertTitle>
          <AlertDescription>
            You can add {remaining} more staff member{remaining !== 1 ? "s" : ""} on your current plan 
            ({currentStaffCount} of {maxStaff} used).
            {" "}
            <Link href="/pricing" className="text-primary hover:underline font-medium">
              Upgrade for unlimited staff
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      ) : (staffList?.length || 0) > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {staffList?.map((member) => {
            const memberStats = staffStats[member.id] || { sent: 0, responses: 0 };
            const responseRate = memberStats.sent > 0 
              ? Math.round((memberStats.responses / memberStats.sent) * 100) 
              : 0;
            
            return (
              <StaffCard
                key={member.id}
                id={member.id}
                name={member.name}
                isActive={member.isActive || false}
                reviewsSent={memberStats.sent}
                responseRate={responseRate}
                onToggleActive={handleToggleActive}
                onShowQR={handleShowQR}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <p className="text-muted-foreground mb-4">No staff members yet</p>
          <Button onClick={() => setAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Staff Member
          </Button>
        </div>
      )}

      <AddStaffDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onAdd={handleAddStaff}
      />

      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="sm:max-w-md">
          {selectedStaff && business && (
            <QRCodeCard
              businessName={business.name}
              staffName={selectedStaff.name}
              url={`${window.location.origin}/r/${business.slug}/${selectedStaff.slug}`}
              brandColor={business.brandColor || "#3B82F6"}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Remove staff member"
        description={`Are you sure you want to remove ${staffToDelete?.name}? This action cannot be undone.`}
        confirmText="Remove"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        loading={deleteStaffMutation.isPending}
      />
    </div>
  );
}
